import { FC, useEffect, useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { OnboardingHighlightOverlay } from './OnboardingHighlightOverlay'
import { OnboardingTooltip } from './OnboardingTooltip'
import studentOnboardingTutorialSelector from '@/redux/selectors/student-onboarding-tutorial'
import {
  advanceStudentOnboardingTutorialStep,
  hideStudentOnboardingTutorialInfoModal,
  setStudentOnboardingTutorialHighlightedElement,
  setStudentOnboardingTutorialCurrentStep,
  setStudentOnboardingTutorialLoading,
  showStudentOnboardingTutorialInfoModal,
  stopStudentOnboardingTutorial,
  setStudentOnboardingTutorialLastTrackedStep,
} from '@/redux/slices/student-onboarding-tutorial'
import {
  StudentOnboardingTutorialStep,
  IStudentOnboardingTutorialStepConfig,
} from '@/types/redux/student-onboarding-tutorial'
import { IOnboardingInfoModalContent } from '@/types/shared'
import { twJoin } from 'tailwind-merge'
import { trackEventMixpanel } from '@/utils/mixpanel'

const Z_INDEX = 660

export const studentOnboardingTutorialStepConfigs: Partial<
  Record<StudentOnboardingTutorialStep, IStudentOnboardingTutorialStepConfig>
> = {
  [StudentOnboardingTutorialStep.AddCourseBtn]: {
    step: StudentOnboardingTutorialStep.AddCourseBtn,
    title: "Let's get started! 🚀",
    description:
      'Click here to add a course and start leveling up your skills today.',
    highlightConfig: {
      elementId: 'add-course-btn',
      backdrop: {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        opacity: 0.1,
      },
      captureClicksOnHighlightedElement: true,
      borderRadius: '100px',
    },
    nextStep: StudentOnboardingTutorialStep.SubjectSelect,
    actionRequired: true, // User can click either next button or the add course button
    autoAdvance: false,
    elementRenderDelayMs: 2000, // wait until dashboard animates
  },
  [StudentOnboardingTutorialStep.SubjectSelect]: {
    step: StudentOnboardingTutorialStep.SubjectSelect,
    title: 'Find your focus',
    description:
      'Select a subject to narrow down the library and find exactly what you need.',
    highlightConfig: {
      elementId: 'subject-list-select',
      backdrop: {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        opacity: 0.1,
      },
      offset: 0,
      padding: 0,
      // captureClicksOnHighlightedElement: true, // used to capture click and
      customBackdrop: ({
        zIndex = 9999,
        backdrop,
        captureClicksOnHighlightedElement = false,
        elementRect,
        highlightStyle,
        targetElementId,
      }) => {
        return (
          <>
            {/* Highlighted area cutout - creates a "hole" in the backdrop */}
            {backdrop && elementRect && (
              <div
                className={twJoin(
                  'absolute',
                  captureClicksOnHighlightedElement
                    ? 'pointer-events-none'
                    : 'pointer-events-none',
                )}
                onClick={
                  captureClicksOnHighlightedElement && targetElementId
                    ? () => {
                        const element = document.getElementById(targetElementId)
                        if (element) {
                          console.debug('element', element)

                          element.click()
                        }
                      }
                    : undefined
                }
                style={{
                  ...highlightStyle,
                  backgroundColor: 'transparent',
                  zIndex: backdrop.zIndex || zIndex,
                  boxShadow: `0 0 0 10000px ${
                    backdrop.backgroundColor || 'rgba(0, 0, 0, 0.5)'
                  }`,
                }}
              />
            )}

            {/* Dark backdrop overlay - covers entire screen */}
            {backdrop && (
              <div
                className="fixed inset-0 pointer-events-none"
                style={{
                  backgroundColor:
                    backdrop.backgroundColor || 'rgba(0, 0, 0, 0.5)',
                  opacity: backdrop.opacity || 1,
                  zIndex: backdrop.zIndex || zIndex,
                }}
              />
            )}
          </>
        )
      },
    },
    nextStep: StudentOnboardingTutorialStep.CoursesList,
    actionRequired: true,
    autoAdvance: false,
    nextButtonText: null,
    elementRenderDelayMs: 1000,
  },
  [StudentOnboardingTutorialStep.CoursesList]: {
    step: StudentOnboardingTutorialStep.CoursesList,
    title: 'Take your pick! 🎓',
    description:
      'Just click on a course to add it to your dashboard and begin your learning adventure.',
    highlightConfig: {
      elementId: 'courses-list-container',
    },
    nextStep: null,
    actionRequired: true,
    autoAdvance: false,
    ignoreOverlay: true,
  },
}

export function generateStudentOnboardingTutorialSteps(
  stepsInOrder: Partial<
    Record<
      StudentOnboardingTutorialStep,
      Partial<IStudentOnboardingTutorialStepConfig>
    >
  >,
): Partial<
  Record<StudentOnboardingTutorialStep, IStudentOnboardingTutorialStepConfig>
> {
  const stepEntries = Object.entries(stepsInOrder)

  return stepEntries.reduce(
    (
      acc: Partial<
        Record<
          StudentOnboardingTutorialStep,
          IStudentOnboardingTutorialStepConfig
        >
      >,
      [step, additionalConfig],
      index,
    ) => {
      acc[step] = {
        ...studentOnboardingTutorialStepConfigs[step],
        ...additionalConfig,
        nextStep: stepEntries[index + 1]?.[0] ?? null,
      }

      return acc
    },
    {},
  )
}

interface Props {
  firstStep?: StudentOnboardingTutorialStep
  stepConfigs?: Partial<
    Record<StudentOnboardingTutorialStep, IStudentOnboardingTutorialStepConfig>
  >
  onComplete?: () => Promise<void>
  analyticsPrefix?: string
}

export const StudentOnboardingTutorial: FC<Props> = ({
  firstStep = StudentOnboardingTutorialStep.AddCourseBtn,
  stepConfigs = studentOnboardingTutorialStepConfigs,
  onComplete,
  analyticsPrefix = 'Student Onboarding',
}) => {
  const dispatch = useDispatch()
  const {
    isActive,
    currentStep,
    highlightedElementId,
    showInfoModal: showModal,
    infoModalContent,
    isLoading,
    lastTrackedStep,
  } = useSelector(studentOnboardingTutorialSelector)

  // Use a ref to guard against duplicate tracking events for the same step and action
  const lastTrackedEventRef = useRef<{
    action: string
    step: string | null
  } | null>(null)

  // Memoize step configs to prevent infinite re-renders
  const memoizedStepConfigs = useMemo(() => stepConfigs, [stepConfigs])

  const stepSequence = useMemo(() => {
    const sequence: StudentOnboardingTutorialStep[] = []
    let step: StudentOnboardingTutorialStep | null = firstStep
    const visited = new Set<StudentOnboardingTutorialStep>()

    while (step && memoizedStepConfigs[step] && !visited.has(step)) {
      visited.add(step)
      sequence.push(step)
      step = memoizedStepConfigs[step]?.nextStep ?? null
    }

    return sequence
  }, [firstStep, memoizedStepConfigs])

  const trackTutorialEvent = useCallback(
    (action: string, extraArgs = {}) => {
      const stepToTrack = currentStep || firstStep
      if (!stepToTrack) return

      // Guard against identical consecutive tracking calls for the same step and action
      // This handles race conditions where handleNextStep might be triggered twice before state updates
      if (
        lastTrackedEventRef.current?.action === action &&
        lastTrackedEventRef.current?.step === stepToTrack
      ) {
        return
      }

      const config = memoizedStepConfigs[stepToTrack]
      if (!config) return

      const stepIndex = stepSequence.indexOf(stepToTrack)
      const totalSteps = stepSequence.length
      const progressPercent =
        totalSteps > 0 ? Math.round(((stepIndex + 1) / totalSteps) * 100) : 0

      const data: Record<string, any> = {
        step_name: stepToTrack,
        step_index: stepIndex + 1,
        total_steps: totalSteps,
        progress_percent: progressPercent,
        title: config.title || config.modalContent?.title,
        is_modal: !!config.modalContent,
        ...extraArgs,
      }

      const eventName = `${analyticsPrefix} Tutorial ${action}`

      trackEventMixpanel(eventName, data)

      // Update the ref to prevent duplicate firing
      lastTrackedEventRef.current = { action, step: stepToTrack }

      console.debug(
        `[${analyticsPrefix.toUpperCase()} TUTORIAL MIXPANEL EVENT]: ${eventName}`,
        { analyticsPrefix, action, eventName, data },
      )
    },
    [
      analyticsPrefix,
      currentStep,
      firstStep,
      memoizedStepConfigs,
      stepSequence,
    ],
  )

  // The header starts the tutorial with its first step already set, so the
  // start branch in handleNextStep never runs: track Started on the first view.
  useEffect(() => {
    if (isActive && currentStep === firstStep && lastTrackedStep !== firstStep) {
      trackTutorialEvent('Started')
    }
  }, [isActive, currentStep, firstStep, lastTrackedStep, trackTutorialEvent])

  // Handle step progression logic
  useEffect(() => {
    if (!isActive || !currentStep) {
      return
    }

    const config = memoizedStepConfigs[currentStep]
    if (!config) {
      return
    }

    // Handle modal display (showAsModal or regular highlight)
    if (config.modalContent) {
      dispatch(setStudentOnboardingTutorialHighlightedElement(null))

      const modalContent: IOnboardingInfoModalContent = {
        ...config.modalContent,
        nextButtonText: config.nextStep
          ? config.nextButtonText === undefined
            ? 'Next'
            : config.nextButtonText
          : config.closeButtonText ?? 'Complete',
        skipButtonText:
          config.skipButtonText === null
            ? null
            : config.skipButtonText === undefined
            ? 'Skip Tutorial'
            : config.skipButtonText,
      }

      dispatch(showStudentOnboardingTutorialInfoModal(modalContent))

      // Track step view for modals (immediate)
      if (currentStep !== lastTrackedStep) {
        trackTutorialEvent('Step Viewed')
        dispatch(setStudentOnboardingTutorialLastTrackedStep(currentStep))
      }
    } else if (config.highlightConfig) {
      // Handle highlight configuration
      dispatch(hideStudentOnboardingTutorialInfoModal())
      dispatch(setStudentOnboardingTutorialHighlightedElement(null))

      const elementId = config.highlightConfig.elementId

      const elementRenderDelayMs = config.elementRenderDelayMs ?? 500

      setTimeout(() => {
        dispatch(setStudentOnboardingTutorialHighlightedElement(elementId))

        const modalContent: IOnboardingInfoModalContent = {
          title: config.title,
          description: config.description,
          nextButtonText: config.nextStep
            ? config.nextButtonText === undefined
              ? 'Next'
              : config.nextButtonText
            : config.closeButtonText ?? 'Complete',
          skipButtonText:
            config.skipButtonText === null
              ? null
              : config.skipButtonText === undefined
              ? 'Skip Tutorial'
              : config.skipButtonText,
        }

        dispatch(showStudentOnboardingTutorialInfoModal(modalContent))

        // Track step view for highlights (after delay)
        if (currentStep !== lastTrackedStep) {
          trackTutorialEvent('Step Viewed')
          dispatch(setStudentOnboardingTutorialLastTrackedStep(currentStep))
        }
      }, elementRenderDelayMs)
    }

    // Auto-advance if configured
    if (config.autoAdvance) {
      const timer = setTimeout(() => {
        if (config.nextStep) {
          dispatch(advanceStudentOnboardingTutorialStep(config.nextStep))
        }
      }, config.elementRenderDelayMs ?? 3000)

      return () => clearTimeout(timer)
    }
  }, [
    isActive,
    currentStep,
    dispatch,
    memoizedStepConfigs,
    lastTrackedStep,
    trackTutorialEvent,
  ])

  const handleComplete = useCallback(async () => {
    try {
      trackTutorialEvent('Completed')
      dispatch(setStudentOnboardingTutorialLoading(true))
      dispatch(stopStudentOnboardingTutorial())

      if (onComplete) {
        await onComplete()
      }
    } finally {
      dispatch(setStudentOnboardingTutorialLoading(false))
    }
  }, [dispatch, onComplete, trackTutorialEvent])

  const handleNextStep = useCallback(async () => {
    // Handle start: if no current step, start with firstStep (initially - AddCourseBtn)
    if (!currentStep) {
      trackTutorialEvent('Started')
      dispatch(setStudentOnboardingTutorialCurrentStep(firstStep))
      return
    }

    const config = memoizedStepConfigs[currentStep!]

    trackTutorialEvent('Step Completed')

    if (!config?.nextStep) {
      await handleComplete()
      return
    }

    dispatch(advanceStudentOnboardingTutorialStep(config.nextStep))

    if (config.highlightConfig?.captureClicksOnHighlightedElement) {
      const element = document.getElementById(config.highlightConfig.elementId)
      if (element) {
        element.click()
      }
    }
  }, [
    currentStep,
    dispatch,
    firstStep,
    handleComplete,
    memoizedStepConfigs,
    trackTutorialEvent,
  ])

  const handleSkipTutorial = useCallback(async () => {
    try {
      trackTutorialEvent('Skipped')
      dispatch(setStudentOnboardingTutorialLoading(true))
      dispatch(stopStudentOnboardingTutorial())

      if (onComplete) {
        await onComplete()
      }
    } finally {
      dispatch(setStudentOnboardingTutorialLoading(false))
    }
  }, [dispatch, onComplete, trackTutorialEvent])

  // Handle user clicking on highlighted elements
  useEffect(() => {
    if (!isActive || !highlightedElementId) {
      return
    }

    const config = currentStep ? memoizedStepConfigs[currentStep] : undefined
    if (!config || !config.actionRequired) return

    const handleElementClick = (event: Event) => {
      const target = event.target as HTMLElement
      const highlightedElement = document.getElementById(highlightedElementId)

      if (
        highlightedElement &&
        (highlightedElement.contains(target) || highlightedElement === target)
      ) {
        setTimeout(() => {
          handleNextStep()
        }, 300)
      }
    }

    document.addEventListener('click', handleElementClick, true)
    return () => {
      document.removeEventListener('click', handleElementClick, true)
    }
  }, [
    isActive,
    highlightedElementId,
    currentStep,
    handleNextStep,
    memoizedStepConfigs,
    dispatch,
  ])

  if (!isActive) {
    return null
  }

  return (
    <>
      {currentStep && !memoizedStepConfigs[currentStep]?.ignoreOverlay && (
        <OnboardingHighlightOverlay
          targetElementId={highlightedElementId}
          isActive={isActive}
          padding={
            currentStep
              ? memoizedStepConfigs[currentStep]?.highlightConfig?.padding
              : undefined
          }
          zIndex={Z_INDEX}
          useBoxShadow={
            currentStep
              ? memoizedStepConfigs[currentStep]?.highlightConfig?.useBoxShadow
              : undefined
          }
          backdrop={
            currentStep
              ? memoizedStepConfigs[currentStep]?.highlightConfig?.backdrop
              : undefined
          }
          captureClicksOnHighlightedElement={
            currentStep
              ? memoizedStepConfigs[currentStep]?.highlightConfig
                  ?.captureClicksOnHighlightedElement
              : undefined
          }
          customBackdrop={
            currentStep
              ? memoizedStepConfigs[currentStep]?.highlightConfig
                  ?.customBackdrop
              : undefined
          }
          borderRadius={
            currentStep
              ? memoizedStepConfigs[currentStep]?.highlightConfig?.borderRadius
              : undefined
          }
          containerClassName="fixed inset-0 pointer-events-auto"
        />
      )}

      <OnboardingTooltip
        targetElementId={highlightedElementId}
        isOpen={showModal}
        content={infoModalContent}
        onNext={infoModalContent?.nextButtonText ? handleNextStep : undefined}
        onSkip={
          infoModalContent?.skipButtonText ? handleSkipTutorial : undefined
        }
        onClose={
          currentStep
            ? memoizedStepConfigs[currentStep]?.hideCloseModalButton
              ? undefined
              : handleSkipTutorial
            : handleSkipTutorial
        }
        isLoading={isLoading}
        showBackdrop={infoModalContent?.showBackdrop ?? false} // used to blur background when in modal mode
        zIndex={Z_INDEX}
        overlayPadding={8}
        preferredTooltipPlacement={
          currentStep
            ? memoizedStepConfigs[currentStep]?.preferredTooltipPlacement
            : undefined
        }
      />
    </>
  )
}
